import { DestinationEvent } from '@eventbrite/event-renderer';
import { QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import mapKeys from 'lodash/mapKeys';
import { useEffect, useMemo } from 'react';
import { OrganicEventSearchFiltersApiPayload } from '../../api/searchOrganicEvents';
import {
    fetchEventUrgencySignals,
    fetchSearchEventExpansions,
} from '../../redux/api/expansions';
import { SearchApiResponse } from '../../redux/api/search';
import { EventSearch } from '../../types';
import { SearchResponseEventsData } from '../../utils/transformation';
import { getEventSearchQueryKey } from './SearchPageData';

export function useEventsUrgencySignals(
    { promoted }: SearchResponseEventsData,
    eventSearch: OrganicEventSearchFiltersApiPayload,
) {
    const eventIds = promoted.map(({ id }) => id);
    const queryClient = useQueryClient();
    const searchQueryKey = useMemo(
        () => getEventSearchQueryKey(eventSearch),
        [eventSearch],
    );

    const { data, ...queryResult } = useQuery({
        queryKey: ['search', 'urgency-signals', ...eventIds],
        queryFn: () => fetchEventUrgencySignals(eventIds),
        staleTime: 3 * 60 * 1000,
        enabled: !isEmpty(promoted),
    });

    useEffect(() => {
        if (!data) {
            return;
        }

        queryClient.setQueryData<SearchApiResponse>(
            searchQueryKey,
            updateEventsWithExpansions(data.events),
        );
    }, [data, promoted, queryClient, searchQueryKey]);

    return { data, ...queryResult };
}
const expansionsQueryKey: QueryKey = ['pageExpansion'];
export function useInitialEventsExpansionQueryState() {
    const queryClient = useQueryClient();
    const state = queryClient.getQueryState(expansionsQueryKey);
    return state?.status;
}

export function useInitialEventsExpansion(
    eventIds: string[],
    eventSearch: EventSearch,
) {
    const queryClient = useQueryClient();
    const queryKey = useMemo(
        () => getEventSearchQueryKey(eventSearch),
        [eventSearch],
    );

    // Fetch expansions for server events
    // The intent here is to manually apply expansions on the events in the store.
    // This only applies to the initial server render.
    // When we call the search endpoint from the user searching
    // in the app, we already get the expansions.
    // Skipping the expansions on the server is a performance optimization.
    // TODO: Unify with event-renderer in the future
    // useMount(async () => {
    // const expansions = await getEventExpansions(initialData);

    const { data: expansions, ...remainingQueryState } = useQuery(
        expansionsQueryKey,
        () => getEventExpansions(eventIds),
        {
            onSuccess: (expansions) => {
                queryClient.setQueryData<SearchApiResponse>(
                    queryKey,
                    updateEventsWithExpansions(expansions),
                );
            },
        },
    );

    return { expansions, ...remainingQueryState };
}

export async function getEventExpansions(
    eventIds: string[],
): Promise<DestinationEvent[]> {
    const { events: eventExpansions } = await fetchSearchEventExpansions(
        eventIds,
    );

    return eventExpansions;
}

function updateEventsWithExpansions(expansions: DestinationEvent[]) {
    // Transform into map for faster lookup in the updater function
    const expansionsMap: Record<string, DestinationEvent> = mapKeys(
        expansions,
        'id',
    );

    return (eventsCache: SearchApiResponse | undefined) => {
        if (!eventsCache) {
            return;
        }
        const { data } = eventsCache;

        const eventUpdater = (event: DestinationEvent) => {
            const expandedData = expansionsMap[event.id];
            if (!expandedData) {
                return event;
            }
            return addExpansionsToEvent(event, expandedData);
        };

        const {
            events: { results = [], promoted_results = [], top_match = [] },
        } = data;
        return {
            ...eventsCache,
            data: {
                ...data,
                events: {
                    ...data.events,
                    results: results.map(eventUpdater),
                    promoted_results: promoted_results.map(eventUpdater),
                    top_match: top_match.map(eventUpdater),
                },
            },
        };
    };
}

function addExpansionsToEvent(
    event: DestinationEvent,
    {
        series = event.series,
        event_sales_status = event.event_sales_status,
        primary_organizer = event.primary_organizer,
        ticket_availability = event.ticket_availability,
        public_collections = event.public_collections,
        urgency_signals = event.urgency_signals,
        saves = event.saves,
    }: DestinationEvent,
) {
    return {
        ...event,
        series,
        event_sales_status,
        primary_organizer,
        ticket_availability,
        public_collections,
        urgency_signals,
        saves,
    };
}
